import {SimpleBlueButton} from '../../components/core/button'
import {getProration, getSubscriptionPayments, updateSubscription} from '../../stores/payment'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {routes} from '../../routes'
import {useEffect, useState} from 'react'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import AmountManager from '../../utils/amount'
import TextHelper from '../../utils/text'
import SkeletonLoader from '../../components/core/skeletonLoader'
import classNames from 'classnames'
import {CurrencyMsgIcon, InfoIcon} from '../../icons'
import NavigationHelper from '../../utils/navigation'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'
import {colors} from '../../utils/colors'

const UpgradeModal = ({setModal, modal, addOns, getUpgradeIsEnabled, findPlanData, findPlanPriceId, paymentInterval, isTrialPeriod, getPriceSymbol, currency, plansData}) => {
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const text = useNamespaceTranslations('plans')
    const subscriptionId = subscriptions?.data?.subscriptions?.find(subscription => subscription?.active === true)?.subscription_id
    const paymentData = useSelector(state => state.payment.paymentData)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const customerCurrency = paymentData?.data?.subscription?.payments?.[0]?.currency
    const [loader, setLoader] = useState(false)
    const [isFetching, setIsFetching] = useState(true)
    const [isUpgradeDisabled, setIsUpgradeDisabled] = useState(false)
    const prorationData = useSelector(state => state.payment.prorationData)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const upgrade = async() => {
        const success = await dispatch(updateSubscription(setLoader, subscriptionId, {
            price_id: modal?.priceId,
            coupon_code: localStorage.getItem('coupon'),
            ...(!isTrialPeriod && {
                add_ons: addOns?.map(addOn => ({
                    price_id: addOn?.prices?.[currency]?.[paymentInterval]?.price_id,
                    quantity: addOn?.quantity
                }))
            })
        }))

        if (success) {
            localStorage.removeItem('coupon')
            setModal(false)
            navigate(`${NavigationHelper.getLocalizedPath(routes.subscription_success.paths)}/?session_id=true`)
            return
        }
        navigate(NavigationHelper.getLocalizedPath(routes.subscription_cancel.paths))
    }

    const getPlanName = (key, interval) => {
        const name = plansData?.find(plan => plan.key === key)?.display_name
        return `${TextHelper.capitalizeFirstChar(name || '')} plan (${interval})`
    }

    const getIntervalPrice = (key, interval, currency) => {
        const plan = findPlanData(key, interval, currency)
        return `${AmountManager.normalizePrice(plan?.amount, getPriceSymbol)}`
    }


    const fetchProrations = async () => {
        const planPriceId = findPlanPriceId(modal?.key, paymentInterval)

        let query = `plan_product_id=${planPriceId}`

        if (addOns?.length > 0) {
            const addOnsArray = addOns
                .filter(addOn => !(isTrialPeriod && addOn.quantity === 0))
                .map(addOn => ({
                    product_id: addOn.prices?.[currency]?.[paymentInterval]?.price_id,
                    quantity: addOn.quantity
                }))
            query += `&add_ons=${encodeURIComponent(JSON.stringify(addOnsArray))}`
        }

        const success =  await dispatch(getProration(setIsFetching, subscriptionId, query))
        if (!success) {
            setIsUpgradeDisabled(true)
        }
    }

    const fetchSubscriptionPayments = async() => {
        await dispatch(getSubscriptionPayments(subscriptionId))
    }

    useEffect(() => {
        if (subscriptionId) {
            fetchSubscriptionPayments()
        }
    }, [subscriptionId])

    useEffect(() => {
        if (!subscriptionId || !modal?.key || !customerCurrency || customerCurrency !== currency) return

        fetchProrations()
    }, [modal, subscriptionId, customerCurrency, paymentInterval])

    return (
        isTrialPeriod && addOns?.some(a => a.quantity > 0) ? (
            <div className={classNames('info-modal', {dark: isDarkMode})}>
                <CurrencyMsgIcon/>
                <span>{text.trial_remove_addons_message}</span>
            </div>
        ) : customerCurrency && customerCurrency !== currency ? (
            <div className={classNames('info-modal', {dark: isDarkMode})}>
                <CurrencyMsgIcon/>
                <span>
                    {text.different_currency_message}
                </span>
            </div>
        ) : isFetching ? (
            <div className="details">
                {Array.from({length: 10}).map((item, index) => (
                    <SkeletonLoader key={index}/>
                ))}
            </div>
        ) : (
            <>
                <div className={classNames('details', {
                    dark: isDarkMode
                })}>
                    <span className="note">
                        <span className="badge">{text.note}</span>
                        {isTrialPeriod ? text.modal_trial_note : text.modal_note}
                    </span>
                    <div className="title">
                        {text.details}
                    </div>
                    <ul>
                        <h4>{text.current_subscription}</h4>
                        {prorationData?.plans?.current?.name &&
                            <>
                                <li className="amount-info">
                                    <span className="plan-name">
                                        {getPlanName(prorationData?.plans?.current?.name, prorationData?.plans?.current?.interval)}
                                    </span>
                                    <span>
                                        {!isTrialPeriod && getIntervalPrice(prorationData?.plans?.current?.name, prorationData?.plans?.current?.interval, prorationData?.plans?.new?.currency)}
                                    </span>
                                </li>
                                <li className="amount-info">
                                    <span>{isTrialPeriod ? text.trial : text.unused}</span>
                                    <span>{isTrialPeriod ? '0' : AmountManager.normalizePrice(prorationData?.plans?.current?.amount, getPriceSymbol)}</span>
                                </li>
                            </>
                        }
                        <h4>{text.current_add_ons}</h4>
                        {prorationData?.add_ons?.current && prorationData.add_ons.current?.length > 0 &&

                            <>
                                {prorationData.add_ons.current.map((addon, index) => (
                                    <li key={index} className="amount-info">
                                        <span className="plan-name">
                                            {addon.name} ({addon.interval})
                                        </span>
                                        <span>
                                            {AmountManager.normalizePrice(addon.amount, getPriceSymbol)}
                                        </span>
                                    </li>
                                ))}
                                <li className="amount-info">
                                    <span>{text.unused}</span>
                                    <span>
                                        {AmountManager.normalizePrice(
                                            prorationData.add_ons.current.reduce((s, a) => s + Math.abs(a.amount), 0),
                                            getPriceSymbol
                                        )}
                                    </span>
                                </li>
                            </>}
                    </ul>
                    <ul>
                        <h4>{text.new_subscription}</h4>
                        {prorationData?.plans?.new?.name &&
                        <>

                            <li className="amount-info">
                                <span className="plan-name">
                                    {getPlanName(prorationData?.plans?.new?.name, prorationData?.plans?.new?.interval)}
                                </span>
                                <span>
                                    {getIntervalPrice(prorationData?.plans?.new?.name, prorationData?.plans?.new?.interval, prorationData?.plans?.new?.currency)}
                                </span>
                            </li>
                            <li className="amount-info">
                                <span>{text.remaining}</span>
                                <span>
                                    {AmountManager.normalizePrice(prorationData.plans.new.amount, getPriceSymbol)}
                                </span>
                            </li>
                        </>}
                        {prorationData?.add_ons?.new && prorationData.add_ons.new?.length > 0 && (
                            <>
                                <h4>{text.current_add_ons}</h4>
                                {prorationData.add_ons.new.map((addon, index) => (
                                    <li key={index} className="amount-info">
                                        <span className="plan-name">
                                            {addon.name} ({addon.interval})
                                        </span>
                                        <span>
                                            {AmountManager.normalizePrice(addon.amount, getPriceSymbol)}
                                        </span>
                                    </li>
                                ))}
                            </>
                        )}
                    </ul>
                    {
                        !isTrialPeriod && (
                            <ul>
                                <li className="amount-info">
                                    <span>{text.excluding_tax}</span>
                                    <span>{prorationData?.difference?.total < 0 ? '- ' : ''}{AmountManager.normalizePrice(prorationData?.difference?.amount, getPriceSymbol)}</span>
                                </li>
                                {prorationData?.difference?.tax?.amount && (
                                    <li className="amount-info">
                                        <span>{text.tax.replace(':percent', prorationData?.difference?.tax?.percent)}</span>
                                        <span>{prorationData?.difference?.total < 0 ? '- ' : ''}{AmountManager.normalizePrice(prorationData?.difference?.tax?.amount, getPriceSymbol)}</span>
                                    </li>
                                )}
                            </ul>
                        )
                    }
                    <ul>
                        <li>
                            <span>{text.total}</span>
                            <span>
                                {isTrialPeriod
                                    ? '0'
                                    : `${prorationData?.difference?.total < 0 ? '- ' : ''}${AmountManager.normalizePrice(prorationData?.difference?.total, getPriceSymbol)}`
                                }
                            </span>
                        </li>
                        {!isTrialPeriod && prorationData?.difference?.total < 0 && (
                            <li className='negative-price'>
                                <InfoIcon color={colors.colorFilterTitle}/>
                                <span  className="note negative-price-info">{text.negative_total_message}</span>
                            </li>
                        )}
                    </ul>
                    {getUpgradeIsEnabled(modal?.key) && (
                        <SimpleBlueButton
                            className="upgrade-button"
                            disabled={loader || isUpgradeDisabled}
                            onClick={upgrade}>
                            {loader && <ButtonLoader/>}
                            {text.upgrade}
                        </SimpleBlueButton>

                    )}
                </div>
            </>
        )
    )
}

export default UpgradeModal
